import { useContext, useEffect, useState } from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import { AuthContext } from "../contexts/AuthContext";
import MicrosoftLogo from "./MicrosoftLogo";
import { SSO_CUTOVER_DATE_LABEL } from "../utils/constants";
import { showErrorNotification } from "../utils/notifications";
import { useSetBannerOffset } from "../contexts/LayoutOffsetContext";

// Per-session dismiss key for the announcement banner.
const DISMISS_KEY = "link_ms_banner_dismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

// Top-of-page announcement banner for JWT-authenticated users with an unlinked
// account, prompting them to link a Microsoft account. Disappears on the next
// /auth/me refresh after a successful link (user.oid set), or when dismissed
// for the session. The link *result* (success or mismatch) is surfaced
// separately as a prominent modal (see MsLinkResultModal).
const LinkMsAccountBanner = () => {
  const { canLinkMsAccount, linkMicrosoftAccount, msalLinkResult } =
    useContext(AuthContext);

  const [dismissed, setDismissed] = useState(readDismissed);

  // canLinkMsAccount is shared with Account Settings; the success result hides the banner before the post-link /auth/me refresh lands.
  const visible =
    canLinkMsAccount && msalLinkResult?.status !== "success" && !dismissed;

  // Publish the banner's live height so viewport-relative page layouts can
  // subtract it (see LayoutOffsetContext); reset to 0 whenever it isn't shown.
  // The wrapper div is measured rather than the Alert so the border-box height
  // (incl. the Alert's own padding) is captured.
  const { ref, height } = useElementSize();
  const setBannerOffset = useSetBannerOffset();
  useEffect(() => {
    setBannerOffset(visible ? height : 0);
    return () => setBannerOffset(0);
  }, [visible, height, setBannerOffset]);

  if (!visible) return null;

  const handleClick = async () => {
    try {
      await linkMicrosoftAccount();
    } catch (error) {
      showErrorNotification({
        title: "Link Microsoft account",
        userMessage:
          error?.message ||
          "Could not start the Microsoft link flow. Please try again.",
      });
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore - banner stays hidden for the lifetime of this component
    }
  };

  return (
    <div ref={ref}>
      <Alert
        color="blue"
        icon={<IconInfoCircle />}
        radius={0}
        withCloseButton
        onClose={handleDismiss}
        data-testid="link-ms-account-banner"
        styles={{
          // Force the icon and body onto the same baseline so the icon sits
          // visually centred against the single-line copy.
          wrapper: { alignItems: "center" },
          body: { alignSelf: "center" },
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="md" align="center">
          <Text size="sm">
            <Text component="span" size="sm" fw={700}>
              Action Required
            </Text>{" "}
            -{" "}
            {SSO_CUTOVER_DATE_LABEL
              ? `Starting ${SSO_CUTOVER_DATE_LABEL}, all users`
              : "All users"}{" "}
            will sign in using Microsoft SSO for improved security and faster
            access. Link your Microsoft account now to ensure a smooth
            transition.
          </Text>
          <Button
            size="xs"
            variant="default"
            leftSection={<MicrosoftLogo size={14} />}
            onClick={handleClick}
            mr="xl"
            // Holds its label intact in narrow viewports instead of being
            // squeezed by the message beside it.
            miw={190}
            style={{ flexShrink: 0 }}
            data-testid="link-ms-account-button"
          >
            Link Microsoft Account
          </Button>
        </Group>
      </Alert>
    </div>
  );
};

export default LinkMsAccountBanner;
